package com.anjuros.website.redesign.sections.blog

import androidx.compose.runtime.Composable
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.components.language.ResKey
import com.anjuros.website.redesign.util.RdImage
import com.anjuros.website.redesign.util.RdText
import com.anjuros.website.templates.selection.modules.BlogCard

/**
 * One article on the Blog page. The article itself (title, intro, date, path and text) comes from [BlogCard];
 * this adds what the redesigned page shows around it. [published] is the ISO date, used for sorting and for Google.
 * [inside] lists a few of the article's headings as "a|b|c". [metaTitle] and [metaDescription] are for the article page,
 * and [wrapUp] is its closing section, shown as a highlighted box with a contact button.
 */
class RdBlogGuide(
    val card: BlogCard,
    val published: String,
    val topic: RdText,
    val image: RdImage.Asset,
    val imageAlt: RdText,
    val inside: RdText,
    val metaTitle: RdText,
    val metaDescription: RdText,
    val wrapUp: ResKey,
)

/** Articles in reading order, as the "Start here" list shows them. Add new articles here. */
val BlogGuides = listOf(
    RdBlogGuide(BlogCard.BLOG1, "2026-07-15", RdText.BLOG_TOPIC_WEBSITES, RdImage.BLOG_WHY_WEBSITE, RdText.BLOG_WHY_WEBSITE_ALT, RdText.BLOG_WHY_WEBSITE_INSIDE,
        RdText.BLOG_WHY_WEBSITE_META_TITLE, RdText.BLOG_WHY_WEBSITE_META_DESCRIPTION, ResKey.BLOG1_SECTION14_TITLE,
    ),
    RdBlogGuide(BlogCard.BLOG3, "2026-08-11", RdText.BLOG_TOPIC_WEBSITES, RdImage.BLOG_FEATURES, RdText.BLOG_FEATURES_ALT, RdText.BLOG_FEATURES_INSIDE,
        RdText.BLOG_FEATURES_META_TITLE, RdText.BLOG_FEATURES_META_DESCRIPTION, ResKey.BLOG3_SECTION15_TITLE,
    ),
    RdBlogGuide(BlogCard.BLOG4, "2026-10-19", RdText.BLOG_TOPIC_WEBSITES, RdImage.BLOG_COST, RdText.BLOG_COST_ALT, RdText.BLOG_COST_INSIDE,
        RdText.BLOG_COST_META_TITLE, RdText.BLOG_COST_META_DESCRIPTION, ResKey.BLOG4_SECTION11_TITLE,
    ),
    RdBlogGuide(BlogCard.BLOG2, "2026-07-22", RdText.BLOG_TOPIC_CUSTOMERS, RdImage.BLOG_MORE_CUSTOMERS, RdText.BLOG_CUSTOMERS_ALT, RdText.BLOG_CUSTOMERS_INSIDE,
        RdText.BLOG_CUSTOMERS_META_TITLE, RdText.BLOG_CUSTOMERS_META_DESCRIPTION, ResKey.BLOG2_SECTION16_TITLE,
    ),
)

/** The most recently published article, shown in the hero. */
val LatestBlogGuide: RdBlogGuide get() = BlogGuides.maxBy { it.published }

private const val WORDS_PER_MINUTE = 200
private val Markup = Regex("""<[^>]+>|\{/?[bi]\}""")
private val Whitespace = Regex("""\s+""")

/** "6 min read", worked out from the article's own text so it stays right when the article changes. */
@Composable
fun blogReadTime(card: BlogCard): String {
    val keys = buildList<ResKey?> {
        add(card.title)
        card.blogSectionList.forEach { section ->
            add(section.sectionTitle)
            section.paragraphList.forEach { paragraph ->
                add(paragraph.paragraphText)
                addAll(paragraph.listPoints)
                paragraph.blogTableColumn.forEach { column ->
                    add(column.columnTitle)
                    addAll(column.columnPoints)
                }
            }
        }
    }
    val words = keys.filterNotNull().sumOf { key ->
        LocalText(key).replace(Markup, " ").trim().split(Whitespace).count { it.isNotEmpty() }
    }
    val minutes = maxOf(1, (words + WORDS_PER_MINUTE / 2) / WORDS_PER_MINUTE)
    return LocalText(RdText.BLOG_READ_TIME).replace("%d", minutes.toString())
}
